import { Link } from 'react-router';
import { autoPayAsks, cardSummary, dueList, dueThisWeek } from '@tahi/core';
import { Money } from '../../../ui';
import { spokenPeso } from '../../../ui/spokenPeso';
import { FOCUS, LABEL } from '../../spending/styles';
import { AutoPayAsk } from '../components/AutoPayAsk';
import { payPath } from '../components/DueRow';
import { copy } from '../copy';
import { dayShort, whenText } from '../format';
import { PAYMENTS_BASE, usePaymentsSnapshot, useToday } from '../hooks';

/** payments.next: an auto-pay question first, else the next (or late) payment. */
export function NextPaymentTile() {
  const snap = usePaymentsSnapshot();
  const today = useToday();
  if (!snap) return <p className="text-sm text-muted">{copy.loading}</p>;
  const list = dueList(snap, today);
  const ask = autoPayAsks(list)[0];
  if (ask) return <AutoPayAsk due={ask} />;
  const next = list[0];
  if (!next) {
    return (
      <Link
        to={PAYMENTS_BASE}
        className={`inline-flex min-h-11 items-center text-sm text-muted ${FOCUS}`}
      >
        {snap.payees.length === 0 ? copy.emptyTitle : copy.allClear}
      </Link>
    );
  }
  const late = list.filter((d) => d.flag === 'late').length;
  return (
    <Link
      to={payPath(next.payeeId, next.period)}
      aria-label={`${copy.nextPayment}: ${next.payeeName}, ${spokenPeso(next.remainingMinor)}, ${whenText(next.daysAway)}`}
      className={`flex flex-col gap-1 rounded-2xl ${FOCUS}`}
    >
      <span className={LABEL}>{next.payeeName}</span>
      <Money
        minor={next.remainingMinor}
        cents="auto"
        className={`block text-[26px] leading-tight ${next.flag === 'late' ? 'text-destructive' : ''}`}
      />
      <span className="text-sm text-muted">
        {dayShort(next.dueDate)} · {whenText(next.daysAway)}
        {next.estimated ? ` · ${copy.estimated}` : ''}
        {late > 1 ? ` · ${copy.lateCount(late)}` : ''}
      </span>
    </Link>
  );
}

/** payments.due-this-week: total and count for the next 7 days (late ones included). */
export function DueThisWeekTile() {
  const snap = usePaymentsSnapshot();
  const today = useToday();
  if (!snap) return <p className="text-sm text-muted">{copy.loading}</p>;
  const week = dueThisWeek(dueList(snap, today, 7));
  return (
    <Link
      to={PAYMENTS_BASE}
      aria-label={`${copy.dueThisWeek}: ${spokenPeso(week.totalMinor)}, ${week.count} payments`}
      className={`flex flex-col gap-1 rounded-2xl ${FOCUS}`}
    >
      <Money minor={week.totalMinor} cents="auto" className="block text-[26px] leading-tight" />
      <span className="text-sm text-muted">
        {week.count === 0
          ? copy.allClear
          : `${week.count} ${week.count === 1 ? 'payment' : 'payments'}`}
      </span>
    </Link>
  );
}

/** payments.cards: each card's balance and next due. */
export function CardsTile() {
  const snap = usePaymentsSnapshot();
  const today = useToday();
  if (!snap) return <p className="text-sm text-muted">{copy.loading}</p>;
  const cards = snap.payees.filter((p) => p.kind === 'card' && !p.deletedAt);
  if (cards.length === 0) return <p className="text-sm text-muted">{copy.noCards}</p>;
  return (
    <ul className="flex flex-col gap-1">
      {cards.map((c) => {
        const s = cardSummary(snap, c, today);
        return (
          <li key={c.id}>
            <Link
              to={`${PAYMENTS_BASE}/p/${c.id}`}
              className={`flex min-h-11 items-center justify-between gap-2 rounded-xl ${FOCUS}`}
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-fg">{c.name}</span>
                {s.nextDue ? (
                  <span className="text-xs text-muted">Due {dayShort(s.nextDue.dueDate)}</span>
                ) : null}
              </span>
              <Money minor={s.balanceMinor} cents="auto" animate={false} className="text-base" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
